import { useState } from 'react';
import { CaretLeft, Money } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { HashLoader } from 'react-spinners';
import { useQueryClient } from 'react-query';
import { useRequestSalaryAdvance } from '../../hooks/useSalaryAdvance';

const REPAYMENT_OPTIONS = [1, 2, 3, 6, 9, 12];

const RequestAdvance = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [months, setMonths] = useState(3);

  const { requestAdvanceAsync, isLoading } = useRequestSalaryAdvance();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = Number(amount.replace(/,/g, ''));
    if (!numAmount || numAmount < 1000) {
      toast.error('Minimum advance amount is ₦1,000');
      return;
    }

    try {
      await requestAdvanceAsync({
        amount: numAmount,
        reason: reason.trim() || undefined,
        repayment_months: months,
      });
      // Bust the cached list so the status page immediately shows the new request
      await queryClient.invalidateQueries(['my-advance-requests']);
      toast.success('Advance request submitted successfully!');
      navigate('status');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to submit request. Please try again.');
    }
  };

  return (
    <div className="flex flex-col items-start gap-5 px-5 py-5 pb-20 max-w-3xl mx-auto w-full">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
      >
        <CaretLeft weight="bold" />
        <span>Back</span>
      </button>

      <div className="bg-white border border-gray-200 rounded-xl w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-gray-100">
          <div className="p-3 bg-visaro-orange/10 rounded-full text-visaro-orange">
            <Money size={28} weight="fill" />
          </div>
          <div>
            <div className="text-xl font-semibold">Request Salary Advance</div>
            <div className="text-sm text-gray-500 mt-0.5">
              Your organisation will review and approve before funds are released.
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* Amount */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              Amount Requested <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                ₦
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setAmount(raw ? Number(raw).toLocaleString() : '');
                }}
                placeholder="0"
                required
                className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 text-lg font-medium focus:outline-none focus:ring-2 focus:ring-deepBlue/30"
              />
            </div>
            <p className="text-xs text-gray-400">Minimum: ₦1,000</p>
          </div>

          {/* Repayment period */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Repayment Period <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {REPAYMENT_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMonths(m)}
                  className={`px-4 py-2 rounded-xl border font-medium text-sm transition ${
                    months === m
                      ? 'bg-deepBlue text-white border-deepBlue'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-deepBlue'
                  }`}
                >
                  {m} {m === 1 ? 'month' : 'months'}
                </button>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Briefly describe what you need the advance for…"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-deepBlue/30 resize-none"
            />
          </div>

          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
            Your request will first be reviewed by your organisation's HR admin.
            Once approved, it will be forwarded to our disbursement partner who
            will release the funds directly to your Visaro wallet.
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-deepBlue text-white rounded-xl py-4 font-semibold text-base disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <HashLoader size={16} color="#ffffff" />
            ) : (
              'Submit Request'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestAdvance;
